/**
 * Tiny keyboard focus trap shared by the portal dialogs (Modal, ChatDrawer).
 * Pointer input already cannot reach the page behind a fixed backdrop, but Tab /
 * Shift+Tab would otherwise walk out of the dialog into the nav, hero buttons, etc.
 */

const FOCUSABLE = ['a[href]', 'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])'].join(',');

/** Visible, enabled, tabbable descendants of `root` in DOM order. */
export function focusableIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.getAttribute('aria-hidden') !== 'true' && el.getClientRects().length > 0);
}

/**
 * Call from a document `keydown` listener while a dialog is open: keeps Tab and
 * Shift+Tab cycling between the first and last focusable element inside `root`.
 * If focus has somehow ended up outside `root`, the next Tab pulls it back in.
 */
export function trapTabKey(root: HTMLElement, e: KeyboardEvent): void {
  if (e.key !== 'Tab') return;
  const items = focusableIn(root);
  if (items.length === 0) {
    e.preventDefault();
    root.focus();
    return;
  }
  const first = items[0] as HTMLElement;
  const last = items[items.length - 1] as HTMLElement;
  const active = document.activeElement as HTMLElement | null;
  const inside = Boolean(active && root.contains(active));
  if (e.shiftKey) {
    if (!inside || active === first) {
      e.preventDefault();
      last.focus();
    }
  } else if (!inside || active === last) {
    e.preventDefault();
    first.focus();
  }
}
